import Vue from 'vue';
import {customAlphabet} from "nanoid";
import Config from '@/config';
import store from '@/store';
import router from '@/router';
import uiMutations from "@/store/ui/mutations";
import {pickSingleValue, PRESETS} from "@/utils/pick";
import {
    applyParentsTo,
    applyToTreeOf,
    applyToNextOf,
    groupByContextId,
    isTaskFullyActive,
    isTaskOfMyAttention,
    isTaskPostponed,
    previewFromContent,
    TABS,
    titleFromContent,
    isTaskStageItemDone,
    PRIOS,
    applyTaskUpdateFromPayload,
    moveTasksInChainContexts,
    applyContextUpdateFromPayload,
    openContextIds,
    appendPayloadWithContextIds,
    applyAreaUpdateFromPayload,
    isTaskComment,
    isTaskDraft,
} from "@/utils/task";
import {parseContextIds} from "@/utils/context";
import {extraTaskHolders} from "@/utils/taskHolders";
import {flatArray, onlyUnique, parentsOf} from "@/utils/utils";
import {posLevel, posParent, posValue} from "@/utils/pos";
import {date2Iso, date2MysqlFull, iso2Date} from "@/utils/date";
import storage from "@/utils/storage";

const findTaskById = (taskId) => {
    // in context tasks
    let task = store.state.page.tasks.flatItems?.find(el => el.id === taskId);
    if (task) return task;

    // in current task
    if (store.state.page.task.item?.id === taskId) return store.state.page.task.item;

    // in current task's children
    task = store.state.page.task.children?.find(el => el.id === taskId);
    return task;
}

const taskIdsFromPayload = (payload) => {
    let taskIds = payload.taskIds;
    if (!taskIds) {
        const taskId = payload.taskId;
        if (!taskId) return;
        taskIds = [taskId];
    }
    return taskIds.filter(onlyUnique);
}

const tasksFromPayload = (payload) => {
    let taskIds = taskIdsFromPayload(payload);
    if (!taskIds?.length) return;

    return taskIds.map(taskId => findTaskById(taskId)).filter(el => !!el);
}

const appendCommentsFromPayload = (state, payload) => {
    if (payload.commentsAppend?.items?.length && state.page.task.item?.id) {
        payload.commentsAppend?.items.forEach(el => {
            if (el.taskId === state.page.task.item?.id) {
                if (!state.page.comments.items) state.page.comments.items = [];
                state.page.comments.items.unshift(el);
            }
        });
    }
}

/**
 * @deprecated - done at server side
 * @param state
 */
/*const updateActiveStageInCurrentTask = (state) => {
    // update activeStage in current task
    const taskId = state.page.task.item.id;
    const index = state.page.task.stages.findIndex((el, index) => !isTaskStageItemDone(index));
    console.log("New task active stage: ", index);
    let params;
    if (index >= 0) {
        params = {
            activeStage: index,
            doneAt: null
        };
    } else {
        // all stages done = task is done!
        params = {
            doneAt: date2Iso(new Date())
        };
    }
    mutations.taskUpdate(state, {
        taskId,
        params
    });
}*/

const updateParentsFromPayload = (state, payload) => {
    //console.log("payload.taskUpdate?.items?.length", payload.taskUpdate?.items?.length);
    if (payload.taskUpdate?.items?.length && state.page.task.item?.id) {
        const item = payload.taskUpdate.items.find(el => el.id === state.page.task.item?.id);
        if (item) {
            Vue.set(state.page.task, "parents", item.parents);
            //Vue.set(state.page.task.item, "groupId", item.groupId);
        }
    }
}

const onTaskProcessed = () => {
    const task = store.state.page.task.item;
    const tasks = store.state.page.tasks.items;
    const context = store.state.page.context;
    if (task && context.item?.isPick && context.item.preset === PRESETS.MY
        && tasks?.length > 0 && store.state.ui.isTaskFullscreen
    ) {
        //console.log("onTaskProcessed", task);
        const plainTasks = state.page.tasks.flatItems;
        const item = plainTasks?.find(el => el.id === task.id);
        if (!item) return;
        //console.log("\titem", item);

        // we take only tasks on same level
        let items;
        if (item?.parent) items = item.parent.children;
        else items = tasks;
        //console.log("items", items);

        const myNextTask = items?.find(el => isTaskOfMyAttention(el));
        //console.log("myNextTask", myNextTask);
        if (!myNextTask) {
            //we've done last task in "my"
            //console.log("Done last task!");
            store.commit("uiMenu", {
                menu: store.state.ui.dialogAllDone,
            });
        }
    }
}

/**
 * Кэш id-ключей flatItems для O(1)-проверки дублей в taskAppendToFlatItems вместо
 * flatItems.find() на каждый добавляемый элемент. Привязан к ссылке на массив -
 * при пересборке flatItems (например, в context/mutations.js contextItem) ссылка
 * меняется и кэш просто перестраивается заново.
 */
let flatItemsIdCache = null;

const mutations = {
    taskItem: (state, payload) => {
        console.log("MUTATION task/item", payload);
        //console.log("MUTATION task/item contextId", payload?.task?.item?.contextId);
        //console.log("MUTATION task/item state.page.task.navPaths", state.page.task.navPaths);
        //console.log("MUTATION task/item contentId", payload?.task?.item?.contentId);

        if (payload.isFromServer) {
            // 1. set parents to navPaths - for convenience
            const navPaths = state.page.task.navPaths;
            if (navPaths?.length) {
                for (let navPath of navPaths) {
                    for (let j = navPath.length - 1; j >= 1; j--) {
                        applyParentsTo([navPath[j]], navPath[j - 1]);
                    }
                }

                // 1. set parents to the task - for convenience
                const parents = navPaths[0];
                if (parents?.length) {
                    //console.log("SETTING PARENT TO THE TASK: "+parents[parents.length-1].id);
                    applyParentsTo([payload.task?.item], parents[parents.length - 1]);
                }
            }
            // 2. set parent to child tasks - for convenience
            //applyParentsTo(payload.task?.item?.children, payload.task?.item);
            applyParentsTo(payload.task?.children, payload.task?.item);
            // 3. we do copy payload.task.children to payload.task.item.children for convenience
            if (state.page.task.item) Vue.set(state.page.task.item, "children", payload.task?.children);

            // 4. set up similarity in task children
            //applySimilarityInTaskChildren(state);

            // 5. set selected task to current task
            if (state.page.task.item) {
                store.commit("contextAppendSelectedTask", {
                    item: state.page.task.item,
                    deleteIfExists: true,
                    withFullReplace: true
                });
            }
        }

        // TODO MOVE TO uiValidateRoute
        // validate route for anon
        /*if (state.session.isAnon) {
            if (router.currentRoute.name === "my-task") {
                if (state.page.task.topic) {
                    router.replace({
                        name: "topic-task",
                        params: {
                            taskId: state.page.task.item.auth,
                            topicAuth: state.page.task.topic.auth,
                        }
                    });
                } else {
                    router.replace({
                        name: "topic-task",
                        params: {
                            taskId: state.page.task.item.auth,
                            groupAuth: state.page.task.group.auth,
                        }
                    });
                }
            }
        }*/
    },
    taskOne: (state, payload) => {
        console.log("MUTATION task/taskOne", payload);

        if (payload?.isFromServer) {
            applyTaskUpdateFromPayload(state, payload);

            // update this.session.oneTask - todo do we need this here? this is done at server side
            /*if ( payload?.task?.item ) {
                console.log("UPDATING SESSION ONE TASK with", payload.task.item);
                update(state.session.oneTask.item, payload.task.item);
            }*/
        } else {
            // do nothing
        }
    },
    taskCreate: (state, payload) => {
        console.log("MUTATION task/create", payload);

        if (payload.isFromServer) {
            // remove archive status from related group
            const group = groupByContextId(payload.contextId);
            if (group) {
                store.commit("contextArchive", {contextId: payload.contextId, isArchive: false});
            }
        } else {
            // add context filters from local storage
            appendPayloadWithContextIds(state, payload);

            // Оптимистичная вставка подзадачи сразу, не дожидаясь ответа сервера. id задачи
            // генерируется здесь же, на FE, и передаётся на BE в params.id - BE использует
            // именно его при создании записи (см. BaseModel.insert()), поэтому у оптимистичного
            // элемента и у настоящего результата всегда один и тот же id. Благодаря этому ответ
            // сервера обрабатывается как taskUpdate (обновление уже показанного элемента живыми
            // данными), а не как taskAppend (добавление ещё одного) - см. TaskModule.create().
            const id = customAlphabet(Config.TASK_ID_ALPHABET, Config.TASK_ID_LENGTH)();
            payload.params = {...payload.params, id};
            payload.mutation = "taskUpdate";

            const optimisticItem = {
                id,
                title: payload.params?.title ?? payload.params?.name,
                _isOptimistic: true,
            };
            mutations.taskAppendToFlatItems(state, optimisticItem);
            mutations.taskPlace(state, {taskId: optimisticItem.id, contextId: payload.contextId, first: !!payload.params?._withFirstPlace});
        }

        // after creation - broadcast to subscribers of this
        /*const broadcast = [{
            entity: "group",
            auth: payload.groupAuth,
        }];
        if (payload.topicAuth) broadcast.push({
            entity: "topic",
            auth: payload.topicAuth,
        });
        payload.broadcast = broadcast;
        console.log("broadcast", broadcast);*/
    },
    taskAppend: (state, payload) => {
        console.log("MUTATION task/append", payload);

        if (payload.isFromServer) {
            // validate payload
            const item = payload?.taskAppend?.item;
            if (!item) return;
            const contextId = payload?.taskAppend?.contextId;
            if (!contextId) return;
            const index = payload.taskAppend?.contextTask?.index;

            // append task to task list
            console.log("Appending task..." + item.id + " to " + contextId + " under index " + index);
            // 1. plain list
            mutations.taskAppendToFlatItems(state, item);   // important to register task before placement
            // 2. to context tree and current task children
            mutations.taskPlace(state, {taskId: item.id, contextId, index});

            // применяем обновлённые счётчики родителя (countAssignableChildren/countDoneChildren/
            // firstAssignableChild), которые вернул сервер - тем же механизмом, что и task/update
            if (payload.taskUpdate?.items?.length) applyTaskUpdateFromPayload(state, payload);
        }
    },
    taskAppendToFlatItems: (state, payload) => {
        //console.log("MUTATION task/appendToFlatItems", payload);

        if (!payload) return;
        if (!Array.isArray(payload)) payload = [payload];
        if (!Array.isArray(state.page.tasks.flatItems)) Vue.set(state.page.tasks, "flatItems", []);

        const flatItems = state.page.tasks.flatItems;
        if (flatItemsIdCache?.ref !== flatItems) {
            flatItemsIdCache = {
                ref: flatItems,
                ids: new Set(flatItems.map(el => el.id + ":" + el.contextId)),
            };
        }

        payload?.forEach(task => {
            const key = task.id + ":" + task.contextId;
            if (flatItemsIdCache.ids.has(key)) return;
            flatItemsIdCache.ids.add(key);
            flatItems.push(task);
        });
    },
    taskUpdate: (state, payload) => {
        console.log("MUTATION task/update", payload);

        //console.log("taskUpdate?", payload.taskUpdate?.item);
        //console.log("topicAppend?", payload.topicAppend?.item);

        if (payload.isFromServer) {
            // ответ на task/create при оптимистичном создании (см. taskCreate()): реальный index задачи в контексте
            // приходит в taskAppend.contextTask - без него новая задача не встанет на свое место в списке (сортировка по index).
            // Кладем index в обновляемую задачу: applyTaskUpdateFromPayload() применит его ко всем экземплярам задачи
            // (в дереве задач контекста стоит КОПИЯ элемента из flatItems - см. taskPlace())
            const createdId = payload.taskAppend?.item?.id;
            const createdIndex = payload.taskAppend?.contextTask?.index;
            if (createdId && typeof createdIndex !== "undefined" && createdIndex !== null) {
                const createdItem = payload.taskUpdate?.items?.find(el => el?.id === createdId);
                if (createdItem) createdItem.index = createdIndex;
            }

            //applyUpdateToCurrentTask(state, payload.taskUpdate);
            applyTaskUpdateFromPayload(state, payload);
            appendCommentsFromPayload(state, payload);
            if (payload.contextUpdate) applyContextUpdateFromPayload(state, payload);
        } else {
            // just for visual speed we apply SOME props straight away... they'll be also properly over-updated after in isFromServer stage

            const taskIds = taskIdsFromPayload(payload);
            if (!taskIds?.length) return;

            let params = {...(payload?.params || {})};
            taskIds.forEach(taskId => applyTaskUpdateFromPayload(state, {
                taskUpdate: {
                    item: {id: taskId, ...params}
                }
            }));

            // add context filters from local storage
            appendPayloadWithContextIds(state, payload);
        }
    },
    taskUpdateContent: (state, payload) => {
        console.log("MUTATION task/updateContent", payload);

        if (payload.isFromServer) {
            // after action
            applyTaskUpdateFromPayload(state, payload);
        } else {
            // before action
        }
    },
    taskDelete: (state, payload) => {
        console.log("MUTATION task/delete", payload);

        /* const contextId = payload.contextId;
         if (!contextId) return;
         if (state.page.context.item?.contextId !== contextId) return;*/

        /*if (state.page.deletedTaskAuths.find(el => el === auth)) throw "Should not delete the task twice";
        state.page.deletedTaskAuths.push(auth);*/

        if (payload.isFromServer) {
            applyTaskUpdateFromPayload(state, payload);
            appendCommentsFromPayload(state, payload);
        } else {
            // add context filters from local storage
            appendPayloadWithContextIds(state, payload);
        }
    },
    taskDestroyIfEmpty: (state, payload) => {
        console.log("MUTATION task/destroyIfEmpty", payload);

        if (payload.isFromServer) {
            applyTaskUpdateFromPayload(state, payload);
            appendCommentsFromPayload(state, payload);
            /*mutations.taskDelete(state, {
                taskIds: payload?.deletedTaskIds,
            });*/
        } else {
            // add context filters from local storage
            appendPayloadWithContextIds(state, payload);
        }
    },
    taskStart: (state, payload) => {
        console.log("MUTATION task/start", payload);

        if (payload.sessionUpdate) {
            if (payload.sessionUpdate.currentTask) Vue.set(state.session.currentTask, "item", payload.sessionUpdate.currentTask);
            if (payload.sessionUpdate.oneTask) Vue.set(state.session.oneTask, "item", payload.sessionUpdate.oneTask);
        }
        if (payload.taskUpdate) {
            // after action
            state.page.task.timer = payload.taskUpdate.timer;
            state.page.task.totalTime = payload.taskUpdate.totalTime;

            appendCommentsFromPayload(state, payload);
        } else {
            // before action
            const timer = state.page.task.timer;
            if (timer) timer.stoppedAt = null;

            //console.log("page task", state.page.task.item);
            Vue.set(state.session.currentTask, "item", state.page.task.item);
            /*console.log("!!!!!!!!!!", state.page.task.timer);
            Vue.set(state.session.currentTask, "item", state.page.task.item);
            Vue.set(state.session.currentTask, "timer", state.page.task.timer);
            Vue.set(state.session.currentTask, "totalTime", state.page.task.totalTime);*/

            //state.ui.isTaskFullscreen = true;
        }

        //console.log("session task", state.session.currentTask.item);

        Vue.set(state.session.currentTask, "timer", state.page.task.timer);
        Vue.set(state.session.currentTask, "totalTime", state.page.task.totalTime);
    },
    taskStop: (state, payload) => {
        console.log("MUTATION task/stop", payload);

        if (payload.taskUpdate) {
            // after action
            state.page.task.timer = payload.taskUpdate.timer;
            ///state.page.task.totalTime = payload.taskUpdate.totalTime;
            appendCommentsFromPayload(state, payload);
        } else {
            // before action
            const timer = state.page.task.timer;
            if (!timer) return;
            timer.stoppedAt = new Date();

            state.session.currentTask = {};

            //state.ui.isTaskFullscreen = false;
        }
    },
    taskStagify: (state, payload) => {
        console.log("MUTATION task/taskStagify", payload);

        if (payload.taskUpdate) {
            // after action

        } else {
            // before action
        }
    },
    taskAddStage: (state, payload) => {
        console.log("MUTATION task/addStage", payload);

        if (payload.isFromServer) {
            // after action
            applyTaskUpdateFromPayload(state, payload);
        } else {
            // before action

        }
    },
    taskUpdateStage: (state, payload) => {
        console.log("MUTATION task/updateStage", payload);

        if (payload.isFromServer) {
            // after action
            applyTaskUpdateFromPayload(state, payload);
        } else {
            // add context filters from local storage
            appendPayloadWithContextIds(state, payload);
        }
    },
    taskDeleteStage: (state, payload) => {
        console.log("MUTATION task/deleteStage", payload);

        if (payload.taskUpdate) {
            applyTaskUpdateFromPayload(state, payload);
            console.log("task/appendStage server action done");
        } else {
            // add context filters from local storage
            appendPayloadWithContextIds(state, payload);
        }
    },
    taskMoveStage: (state, payload) => {
        console.log("MUTATION task/moveStage", payload);

        if (payload.isFromServer) {
            // after action
            applyTaskUpdateFromPayload(state, payload);
        } else {
            // before action
            /*const stages = state.page.task.stages;
            if (stages?.length) {
                const t = stages.splice(payload.stage, 1);
                stages.splice(payload.toStage, 0, t[0]);
            }
            console.log("stages after move", stages.map(el => el.content));
*/            //updateActiveStageInCurrentTask(state);
        }
    },
    /*taskStageDone: (state, payload) => {
        console.log("MUTATION task/stageDone", payload);

        if (payload.taskUpdate) {
            // after action
        } else {
            // before action
            const taskId = payload.taskId;
            const stageIndex = payload.stage;

            if (state.page.task.item?.id === taskId
                && state.page.task.item.totalStages > 1
                && state.page.task.item.stage === stageIndex
            ) {
                //state.page.task.item.activeStage++;
                if ( state.page.task.stages?.length>stageIndex ) {
                    state.page.task.stages[stageIndex].stageDoneAt = date2Iso(new Date());
                }
                //const stageIndex
            }
        }
    },*/
    taskRestore: (state, payload) => {
        console.log("MUTATION task/restore", payload);

        mutations.taskUpdate(state, {
            ...payload,
            params: {
                deletedAt: null,
                doneAt: null,
                deferredAt: null,
                delayedTill: null,
                isDelayed: false,
            }
        });
    },
    taskCreateThread: (state, payload) => {
        console.log("MUTATION task/createThread", payload);
        //payload.mutation = "taskCreateThread";

        if (payload.isFromServer) {
            applyTaskUpdateFromPayload(state, payload);
        }
    },
    taskDeleteThread: (state, payload) => {
        console.log("MUTATION task/deleteThread", payload);

        if (payload.isFromServer) {
            applyTaskUpdateFromPayload(state, payload);
        }
    },
    taskAccess: (state, payload) => {
        console.log("MUTATION task/access", payload);

        if (payload.isFromServer) {
            applyTaskUpdateFromPayload(state, payload);
            appendCommentsFromPayload(state, payload);
            applyAreaUpdateFromPayload(state, payload);
        } else {
            // add context filters from local storage
            appendPayloadWithContextIds(state, payload);
        }
    },
    taskGrant: (state, payload) => {
        console.log("MUTATION task/grant", payload);

        if (payload.isFromServer) {
            applyTaskUpdateFromPayload(state, payload);
            applyAreaUpdateFromPayload(state, payload);
        } else {
        }
    },
    taskRevoke: (state, payload) => {
        console.log("MUTATION task/revoke", payload);

        if (payload.isFromServer) {
            applyTaskUpdateFromPayload(state, payload);
            //applyAreaUpdateFromPayload(state, payload);
        } else {
        }
    },
    /**
     * @deprecated consider using taskUpdate
     * @param state
     * @param payload
     */
    taskAssign: (state, payload) => {
        console.log("MUTATION task/assign", payload);

        if (payload.isFromServer) {
            applyTaskUpdateFromPayload(state, payload);
            appendCommentsFromPayload(state, payload);
            /*if (payload.commentsAppend?.items?.length && state.page.task.item?.id) {
                console.log("commentsAppend", payload);
                payload.commentsAppend?.items.forEach(el => {
                    if (el.taskId === state.page.task.item?.id) {
                        if (!state.page.comments.items) state.page.comments.items = [];
                        state.page.comments.items.unshift(el);
                    }
                });
            }*/
        } else {
            // add context filters from local storage
            appendPayloadWithContextIds(state, payload);

            // just for visual speed we apply SOME props straight away... they'll be also properly over-updated after in isFromServer stage
            const taskIds = taskIdsFromPayload(payload);
            if (!taskIds?.length) return;
            let params = {};
            if (typeof payload.userId != "undefined") params.userId = payload.userId;
            if (typeof payload.ownerId != "undefined") params.ownerId = payload.ownerId;
            taskIds.forEach(taskId => applyTaskUpdateFromPayload(state, {
                taskUpdate: {
                    item: {id: taskId, ...params}
                }
            }));
        }
    },
    taskCompanion: (state, payload) => {
        console.log("MUTATION task/companion", payload);
        if (!payload?.taskId) return;

        //if (!payload?.assigneeAuth&&!payload?.ownerAuth) return;

        const taskIds = taskIdsFromPayload(payload);
        if (!taskIds?.length) return;

        const userAuth = payload.userAuth;
        const companionAuth = payload.companionAuth;

        // we can only add a user we know
        if (userAuth) {
            const user = state.session.area.users.find(el => el.id === userAuth);
            if (!user) return;
            //console.log("USER FOUND", user);
        }

        const pushCompanion = (task, userAuth) => {
            let companionAuths = task.companions || [];
            if (typeof companionAuths === "string") companionAuths = JSON.parse(companionAuths);

            if (companionAuth) {
                const index = companionAuths.findIndex(el => el === companionAuth);
                if (index >= 0) {
                    if (userAuth) companionAuths.splice(index, 1, userAuth);
                    else companionAuths.splice(index, 1);
                }
            } else if (userAuth && !companionAuths.includes(userAuth)) companionAuths.push(userAuth);

            Vue.set(task, "companions", companionAuths);
        }

        let items = state.page.tasks.flatItems;
        taskIds.forEach(taskId => {

            // update item in a context
            let item;
            if (items?.length) {
                item = items.find(el => el.id === taskId);
                if (item) {
                    //console.log("Item in list found", item);
                    pushCompanion(item, userAuth);
                }
            }

            // update current item
            if (state.page.task.item && state.page.task.item.id === taskId) {
                //console.log("Current item found", state.page.task.item);
                pushCompanion(state.page.task.item, userAuth);
            }

        });

        // broadcast to subscribers of this
        /*item = item || state.page.task.item;
        if (item) {
            //payload.mutation = "task/update";
            /!*payload.params = {
                taskAuth: item.auth,
                params: {
                    companions: item.companions,
                }
            }*!/
            payload.broadcasts = _broadcastsForTask(state, item);
            //console.log("broadcast", payload.broadcasts);

            if (payload.initiatorAuth) {
                uiMutations.uiSetActorOfTask(state, {
                    userAuth: payload.userAuth,
                    subjectAuth: item.auth,
                    icon: "mdi-at",
                });
            }
        } else {
            // is this a new task?
        }*/
    },
    taskTemplate: (state, payload) => {
        console.log("MUTATION task/template", payload);
        mutations.taskUpdate(state, payload);
    },

    /**
     * Places task(s) into specified context (tree level) as a child of this context.
     * Task(s) _must_ already exist in flatItems to be placed.
     * payload: {
     * taskIds,
     * contextId,
     * index
     * }
     * @param state
     * @param payload
     */
    taskPlace: (state, payload) => {
        console.log("MUTATION task/place", payload);

        // we use context only for access validation
        /*const contextId = payload.contextId;
        if (!contextId) return;
        if (state.page.context.item?.contextId !== contextId) return;*/

        if (payload.isFromServer) {
            applyTaskUpdateFromPayload(state, payload);
            appendCommentsFromPayload(state, payload);
            updateParentsFromPayload(state, payload);

            if (payload.taskUpdate?.navPaths) {
                payload.taskUpdate?.items?.forEach(task => {
                    if (state.page.task.item?.id === task.id) {
                        state.page.task.navPaths = payload.taskUpdate?.navPaths;
                    }
                });
            }
        } else {
            let taskIds = taskIdsFromPayload(payload);
            if (!taskIds?.length) return;

            //console.log("taskIdsFromPayload", taskIds);

            const contextId = payload.contextId;
            const index = payload.index;
            const first = payload.first;

            const placeInTree = (tree, task, topParent, index) => {
                //console.log("placeInTree", tree?.length, contextId, task.id, topParent?.id);
                if (!tree) return;

                // 2. place into new parent
                let newSiblings, newIndex;
                //console.log("placeInTree() topParent.id", topParent?.id);
                //console.log("placeInTree() contextId", contextId);
                if (topParent?.id === contextId) {
                    // new context is a root context
                    console.log("Placing to root", topParent.id);
                    newSiblings = tree;
                    task.contextId = contextId;
                    task.parent = topParent;

                    if (first) {
                        // ставим первым немедленно (реальный index на BE ещё неизвестен - см. Tasks.create())
                        tree.unshift(task);
                    } else if (typeof index != "undefined") {
                        // place at specified index
                        const posIndex = tree.findIndex(el => el.index >= index);
                        console.log("placeInTree() found index: "+posIndex);
                        if (posIndex >= 0) tree.splice(posIndex, 0, task);
                        else tree.push(task);
                    } else {
                        // place as first
                        tree.push(task);
                    }
                } else {
                    console.log("Placing into context " + contextId);
                    const plainList = flatArray(tree); // important to flat this local tree, neither state.page.tasks.items nor state.page.task.children;
                    let newContexts = plainList.filter(el => el.id === contextId);
                    newContexts?.forEach(newContext => {
                        // new context is a task
                        // if folder is open but children are yet empty - we have to init children array
                        if (!newContext.countChildren) {
                            //console.log("!newContext.countChildren");
                            //let v = storage.get(Config.STORAGE_OPEN_CONTEXTS);
                            Vue.set(newContext, "children", []);
                            /*if (v?.includes(newContext.id)) {
                                console.log("newContext is open: " + newContext.id);
                            }*/
                        }
                        if (newContext.children) {
                            //console.log("newContext.children");
                            if (first) {
                                // ставим первым немедленно (реальный index на BE ещё неизвестен - см. Tasks.create())
                                newContext.children.unshift(task);
                            } else if (typeof index != "undefined") {
                                const posIndex = newContext.children.findIndex(el => el.index === index);
                                if (posIndex >= 0) newContext.children.splice(posIndex, 0, task);
                                else newContext.children.push(task);
                            } else newContext.children.push(task);
                            Vue.set(newContext, "countChildren", newContext.children.length);

                            newSiblings = newContext.children;
                            task.parent = newContext;
                            task.contextId = newContext.id;
                        } else {
                            //console.log("anyway - set children count non zero");
                            // anyway - set children count non zero
                            Vue.set(newContext, "countChildren", 1);
                        }
                    });
                }

                // todo decide index
                /*if (beforeId) newIndex = newSiblings.findIndex(el => el.id === beforeId);
                else if (typeof payload.newIndex != "undefined") newIndex = payload.newIndex;
                else newIndex = newSiblings.length; // end of list
                /!*const newParentPos = posParent(pos);
                if (newParentPos) {
                    const newParent = items.find(el => el.pos === newParentPos);
                    if (newParent) {
                        if (!newParent.children) Vue.set(newParent, "children", []);
                        newSiblings = newParent.children;
                    }
                }*!/
                //if (newIndex > newSiblings.length) newIndex = newSiblings.length;
                //console.log("NEW INDEX", newIndex);
                if (newIndex >= 0) newSiblings.splice(newIndex, 0, ...tasks);*/
            }

            taskIds.forEach(taskId => {
                // 1. do we have access to task?
                let task = findTaskById(taskId);
                if (!task) {
                    console.warn("Task not found in flat items: " + taskId);
                    return;
                }
                task = {...task};   // copy from flat items so it todo why copy?

                // 2. remove task if it already exists in the specified context
                mutations.taskRemove(state, {taskId: task.id, contextId: payload.contextId});

                // 3. place into context if applicable
                if (!state.page.tasks.items) Vue.set(state.page.tasks, "items", []);
                if (!isTaskComment(task)) {
                    placeInTree(state.page.tasks.items, task, state.page.context.item, index);
                    //placeInTree(state.page.tasks.flatItems, task, state.page.context.item);
                    //mutations.taskAppendToFlatItems(state, task);
                }

                // 4. place into current task children if applicable
                //console.log("PLACING INTO CHILDREN", state.page.task.item?.id)
                console.log("state.page.task.item?.id: " + state.page.task.item?.id);
                console.log("payload.contextId: " + payload.contextId);
                if (state.page.task.item?.id === payload.contextId) {
                    if (!state.page.task.children) Vue.set(state.page.task, "children", []);
                    console.log("\tplacing as child to current task.id: " + state.page.task.item?.id);
                    placeInTree(state.page.task.children, task, state.page.task.item, index);
                }

                // 5. то же для задач панелей с собственными данными (комментарии, черновики)
                extraTaskHolders(state).forEach(holder => {
                    if (holder.item.id !== payload.contextId) return;
                    if (!holder.children) Vue.set(holder, "children", []);
                    placeInTree(holder.children, {...task}, holder.item, index);
                });
            });
        }
    },
    taskMove: (state, payload) => {
        console.log("MUTATION task/move", payload);

        if (payload.isFromServer) {
            appendCommentsFromPayload(state, payload);
            updateParentsFromPayload(state, payload);

            // move tasks
            let tasks = payload.taskMove?.items;
            tasks?.forEach(xt => {
                if (xt.fromContextId !== xt.contextId) {
                    console.log("Moving context task [" + xt.taskId + "] from " + xt.fromContextId + " to " + xt.contextId);
                    // чисто пользовательская задача остаётся в исходном пользовательском контексте
                    // (в дереве пользователя иерархия важнее: задача уходит из "Без проекта" в проект)
                    if (!xt.keptInSource || state.page.context.userTree) {
                        mutations.taskRemove(state, {taskId: xt.taskId, contextId: xt.fromContextId});
                    }
                    mutations.taskPlace(state, {taskId: xt.taskId, contextId: xt.contextId});
                }
            });

            // списки задач контекстов цепочки (панели): перенос между контекстами до применения индексов
            moveTasksInChainContexts(state, tasks, undefined, payload);

            applyTaskUpdateFromPayload(state, payload);

            // apply nav path
            if (payload.taskUpdate?.navPaths) {
                //console.log("!!!", payload.taskUpdate?.navPaths);
                //console.log("!!! 2", state.page.task.item?.id);
                //console.log("!!! 3", payload.taskMove?.items[0].taskId);
                if (payload.taskMove?.items?.length === 1 && state.page.task.item?.id === payload.taskMove?.items[0].taskId) {
                    Vue.set(state.page.task, "navPaths", payload.taskUpdate?.navPaths);
                }
                /*payload.taskUpdate?.navPaths?.forEach(xt => {
                    if (state.page.task.item?.id === xt.taskId) {
                        state.page.task.navPaths = payload.taskUpdate?.navPaths;
                    }
                });*/
            }
        } else {
            // add context filters from local storage
            appendPayloadWithContextIds(state, payload);

            // оптимистично показываем перенос/перестановку в списках задач контекстов цепочки (панели)
            moveTasksInChainContexts(state, payload.taskIds?.map((taskId, i) => ({
                taskId,
                fromContextId: payload.fromContextIds?.[i] ?? payload.fromContextId,
                contextId: payload.contextId,
            })), payload.index);
        }
    },
    taskRemove: (state, payload) => {
        console.log("MUTATION task/remove", payload);

        if (payload.isFromServer) {
            applyTaskUpdateFromPayload(state, payload);
            appendCommentsFromPayload(state, payload);
            updateParentsFromPayload(state, payload);

            // todo move this to a routine applyNavPathUpdateFromPayload() or to applyTaskUpdateFromPayload()
            if (payload.taskUpdate?.navPaths) {
                payload.taskUpdate?.items?.forEach(task => {
                    if (state.page.task.item?.id === task.id) {
                        state.page.task.navPaths = payload.taskUpdate?.navPaths;
                    }
                });
            }
        } else {
            let taskIds = taskIdsFromPayload(payload);
            if (!taskIds?.length) return;
            //console.log("taskIdsFromPayload", taskIds);

            const contextId = payload.contextId;
            /*console.log("contextId", contextId);
            console.log("state.page.context.item?.isGroup", state.page.context.item?.isGroup);
            console.log("state.page.context.item?.auth", state.page.context.item?.auth);*/

            const removeInTree = (taskId, tree) => {
                if (!tree?.length) return;
                const items = flatArray(tree);

                //console.log(items.map(el => el.contextId + ":" + el.id + ":" + el.title));

                // remove in context
                const contextTasks = items?.filter(el => el.id === taskId && el.contextId === contextId);
                contextTasks?.forEach(contextTask => {
                    const plainIndex = items?.findIndex(el => el.id === taskId && el.contextId === contextId);
                    if (!contextTask) return;

                    // remove from plain list
                    //console.log("Found in list", contextTask);
                    items.splice(plainIndex, 1);

                    // remove in context
                    const parentList = contextTask.parent?.children || state.page.tasks.items;
                    let index = parentList?.findIndex(el => el.id === taskId);
                    if (index >= 0) {
                        parentList.splice(index, 1);
                        //console.log("REMOVED FROM context");
                    }
                });
            }

            // cut tasks from context
            taskIds.forEach(taskId => {
                removeInTree(taskId, state.page.tasks.items);
                removeInTree(taskId, state.page.tasks.flatItems);
                removeInTree(taskId, state.page.task.item?.children);
                extraTaskHolders(state).forEach(holder => removeInTree(taskId, holder.children));

                // remove in children of the current task
                /*if (state.page.task.item?.id === contextId) {
                    const flatChildren = flatArray(state.page.task.item.children);
                    const task = flatChildren?.find(el => el.id === taskId && el.contextId === contextId);
                    if (task?.parent) {
                        const index = task.parent?.children?.findIndex(el => el.id === taskId && el.contextId === contextId);
                        if (index >= 0) {
                            task.parent.children.splice(index, 1);
                            //console.log("REMOVED FROM context");
                        }
                    }
                }*/
            });
        }
    },
    taskPublish: (state, payload) => {
        console.log("MUTATION task/publish", payload);

        if (payload.isFromServer) {
            applyTaskUpdateFromPayload(state, payload);
            updateParentsFromPayload(state, payload);
            //applySimilarityInTaskChildren(state);
            if (payload.contextUpdate) applyContextUpdateFromPayload(state, payload);
        }
        else {
            // add context filters from local storage
            appendPayloadWithContextIds(state, payload);
        }
    },

    // todo review

    /**
     * A task moved to a context (eg. as a result of DND over context).
     * @deprecated - check if this is used
     * @param state
     * @param payload
     */
    taskContext: (state, payload) => {
        console.log("MUTATION task/context", payload);
        if (!payload.taskIds) return;

        const taskIds = payload.taskIds;
        const targetContextId = payload.contextId;
        const items = state.page.tasks.items;
        const currentContext = state.page.context.item;
        const area = state.session.area;
        //console.log("items", items);

        const {rootContextId, contextId} = parseContextIds(targetContextId);
        const contexts = [
            ...area.picks,
            ...area.favoriteUsers,
            ...area.groups,
            ...area.topics,
            ...area.tags,
        ];
        const newContext = contexts.find(el => el.contextId === contextId);
        if (!newContext) return;

        //console.log("newContext", newContext);

        const proxyToTaskAssign = (assigneeAuth) => {
            // we go to task/assign
            mutations.taskAssign(state, {
                taskIds,
                assigneeAuth
            });
        }
        const proxyToTaskTag = (tagId) => {
            return; // todo are we getting rid of tags?

            // we go to task/tag
            payload.tagId = tagId;
            payload.mutation = "taskTag";
            mutations.taskTag(state, payload);
        }

        let newGroup, newTopic, newUser;
        if (newContext.isGroup) newGroup = newContext;
        else if (newContext.isTopic) {
            newTopic = newContext;
            newGroup = area.groups.find(el => el.id === newTopic.groupId);
        } else if (newContext.isPick) {
            const groupId = pickSingleValue(newContext, "groupId");
            if (groupId) newGroup = newContext;
            else {
                const topicId = pickSingleValue(newContext, "topicId");
                if (topicId) newTopic = newContext;
                else {
                    const userId = pickSingleValue(newContext, "userId");
                    if (userId) {
                        const user = area.users.find(el => el.id === userId);
                        return proxyToTaskAssign(user.id);
                    }
                }
            }
        } else if (newContext.isUser) return proxyToTaskAssign(newContext.contextId);
        else if (newContext.isTag) return proxyToTaskTag(newContext.id);

        // 1. remove from current context
        taskIds.forEach(taskId => {
            let task;
            if (items?.length) {
                const index = items.findIndex(el => el.id === taskId);
                if (index >= 0) {
                    task = items[index];

                    const oldGroup = area.groups.find(el => el.id === task.groupId);
                    let oldTopic;
                    if (task.topicId) oldTopic = area.topics.find(el => el.id === task.topicId);

                    console.group("Moving task: " + task.auth);
                    console.log("Current context:" + currentContext.name);
                    console.log("Current context isGroup:" + currentContext.isGroup);
                    console.log("Current context isTopic:" + currentContext.isTopic);
                    console.log("Current context isTag:" + currentContext.isTag);
                    console.log("Current context isPick:" + currentContext.isPick);
                    console.log("Current context isUser:" + currentContext.isUser);
                    console.log("Old group: " + oldGroup.name);
                    if (oldTopic) console.log("Old topic: " + oldTopic.name);
                    if (newGroup) console.log("New group: " + newGroup.name);
                    if (newTopic) console.log("New topic: " + newTopic.name);
                    console.groupEnd();

                    //console.log("route", router.currentRoute.name);
                    const currentRoute = router.currentRoute.name;

                    if (currentRoute === "context" || currentRoute === "context-task") {

                        // TODO rewrite with smth like isTaskSatisfyingToContextConds(task, context);

                        if ((currentContext.isTopic && newTopic && newTopic.id !== currentContext?.id)
                            || (currentContext.isTopic && !newTopic)
                            || (currentContext.isGroup && newGroup && newGroup.id !== oldGroup.id)
                            || (currentContext.isPick && newGroup && newGroup.id !== oldGroup.id)   // this is not enough and can be a bug! // TODO rewrite with smth like isTaskSatisfyingToContextConds(task, context);
                        ) {
                            console.log("REMOVING ITEM", task);

                            const action = () => {
                                items.splice(index, 1);
                            };

                            if (payload.initiatorAuth) {
                                uiMutations.uiSetActorOfTask(state, {
                                    userAuth: payload.userAuth,
                                    subjectAuth: task.auth,
                                    icon: "mdi-close",
                                });
                                setTimeout(action, 600);
                            } else action();
                            //items.splice(index, 1);
                        } else {
                            // skip removing from same group/topic
                            console.log("Skipped removing from same group/topic");
                        }
                    }
                }
            }

            if (task) {
                // 1. apply task changes
                if (newContext.isGroup || newContext.isTopic) {
                    task.groupId = newGroup.id;
                    task.topicId = newTopic?.id;
                }

                // 1.5 update current item
                if (state.page.task.item && state.page.task.item.id === task.id) {
                    state.page.task.item.groupId = newGroup.id;
                    state.page.task.item.topicId = newTopic?.id;
                }

                // 2. broadcast
                const broadcasts = [];
                const group = state.session.area.groups.find(el => el.id === task.groupId);
                if (group) {
                    broadcasts.push({
                        entity: "group",
                        auth: group.auth,
                    });
                    const topic = task.topicId ? state.session.area.topics.find(el => el.id === task.topicId) : null;
                    if (topic) broadcasts.push({
                        entity: "topic",
                        auth: topic.auth,
                    });
                }
                if (newGroup) {
                    broadcasts.push({
                        entity: "group",
                        auth: newGroup.auth,
                    });
                }
                if (newTopic) broadcasts.push({
                    entity: "topic",
                    auth: newTopic.auth,
                });

                if (broadcasts.length) payload.broadcasts = broadcasts;
                console.log("broadcasts", broadcasts);

                uiMutations.uiSetActorOfGroup(state, {
                    userAuth: payload.userAuth,
                    subjectAuth: newTopic?.auth || newGroup?.auth,
                    icon: "mdi-flare",
                });
            }
        });

        // 2. todo append current tasks
        if (payload.newTask) {
            const group = state.page.context.group;
            const topic = state.page.context.topic;
            if ((payload.newTask.topicId && topic && payload.newTask.topicId === topic.id)
                || (payload.newTask.groupId && !topic && group && payload.newTask.groupId === group.id)) {
                //console.log("NEW TASK", payload.newTask);
                mutations.taskAppend(state, {
                    task: {
                        item: payload.newTask
                    }
                });

                uiMutations.uiSetActorOfTask(state, {
                    userAuth: payload.userAuth,
                    subjectAuth: payload.newTask.auth,
                    icon: "mdi-flare",
                });
            }
        }

    },
    /**
     * Changes task sorting between two tasks in the specified context.
     * @param state
     * @param payload
     */
    taskSort: (state, payload) => {
        console.log("MUTATION task/sort", payload);

        const sources = payload.sources;
        if (!sources?.length) return;
        const destinationAuth = payload.destinationAuth;
        if (!destinationAuth) return;

        const items = state.page.tasks.items;

        // TODO check sort context is appropriate, for instance pick vs group sorting
        const contextId = payload.contextId;
        const context = state.page.context.item;

        if (items?.length) {
            //console.log("items", items);
            let destinationIndex = items.findIndex(el => el.auth === destinationAuth);
            if (destinationIndex !== -1) {
                const sourcesSorted = [...sources];
                sourcesSorted.sort((a, b) => {
                    const aIndex = items.findIndex(el => el.auth === a);
                    const bIndex = items.findIndex(el => el.auth === b);
                    return aIndex - bIndex;
                });
                let lastSource;
                sourcesSorted.forEach((sourceAuth, index) => {
                    const sourceIndex = items.findIndex(el => el.auth === sourceAuth);
                    if (sourceIndex !== -1) {
                        const source = items[sourceIndex];
                        items.splice(sourceIndex, 1);
                        if (lastSource) {
                            const lastSourceIndex = items.findIndex(el => el.auth === lastSource.auth);
                            items.splice(lastSourceIndex + 1, 0, source);
                        } else items.splice(destinationIndex, 0, source);
                        lastSource = source;
                        //destinationIndex = items.findIndex(el => el.auth === destinationAuth);
                        //if (sourceIndex > destinationIndex) destinationIndex++;
                    }
                });
            }
        }

        /*if (context?.contextId === contextId) {
            let taskSort;
            if (context.isGroup) taskSort = context.taskSort;
            else if (context.isTag || context.isTopic) {
                // todo obtain parent group
            }
            if (taskSort) {
                //console.log("taskSort", taskSort);
            }
        }*/
    },
    /**
     * Applies a tag to a task.
     * Updates task text.
     * @param state
     * @param payload
     */
    taskTag: (state, payload) => {
        console.log("MUTATION task/tag", payload);
        if (!payload?.appendTags) return;
        if (!payload?.appendTags.taskAuth) return;
        //if (!payload?.updateTags.tags) return;
        //if (!payload?.assigneeAuth&&!payload?.ownerAuth) return;

        const taskAuth = payload.appendTags.taskAuth;
        const tags = payload.appendTags.tags;
        if (!tags?.length) return;

        const area = state.session.area;
        tags.forEach(tag => {
            if (!area.tags.find(el => el.id === tag.id)) area.tags.push(tag);
        });
        console.log("TASK TAGS", tags);

        const updateTask = (task) => {
            task.tagIds = task.tagIds || [];
            tags.forEach(tag => {
                if (!task.tagIds.includes(tag.id)) task.tagIds.push(tag.id);
            });
        }

        // update item in a context
        const items = state.page.tasks.items;
        let item;
        if (items?.length) {
            item = items.find(el => el.auth === taskAuth);
            if (item) updateTask(item);
        }
        console.log("taskTag", taskAuth, state.page.task.item);
        if (state.page.task.item && state.page.task.item.auth === taskAuth) {
            updateTask(state.page.task.item);
            if (!item) item = state.page.task.item;
        }

        if (item) {

            //payload.mutation = "taskUpdateTags";

            // update editor doc
            /*const provider = new HocuspocusProvider({
                url: Config.API_SERVER_WS_EDITOR_URI,
                name: taskAuth,
                onConnect() {
                    const doc = provider.document;
                    console.log("Provider connected: "+taskAuth, provider.document);

                    // TODO apply to live editor
                    //Y.applyUpdate(doc, diff2)
                },
            });*/

            // broadcast to subscribers of this
            payload.broadcasts = _broadcastsForTask(state, item);
            console.log("broadcast", payload.broadcasts);

            if (payload.initiatorAuth) {
                uiMutations.uiSetActorOfTask(state, {
                    userAuth: payload.userAuth,
                    subjectAuth: item.auth,
                    icon: "mdi-at",
                });
            }
        }
    },
    taskUntag: (state, payload) => {
        console.log("MUTATION task/untag", payload);
        if (!payload?.taskAuth) return;
        if (!payload?.tagId) return;
        //if (!payload?.assigneeAuth&&!payload?.ownerAuth) return;

        const taskAuth = payload.taskAuth;
        const tagId = payload?.tagId;
        const tag = state.session.area.tags?.find(el => el.id === tagId);
        if (!tag) return;

        //console.log("TAG", tag);

        // update item in a context
        const items = state.page.tasks.items;
        let item;
        if (items?.length) {
            item = items.find(el => el.auth === taskAuth);
            //if (item) updateTask(item);
        }
        if (!item) {
            if (state.page.task.item && state.page.task.item.auth === taskAuth) item = state.page.task.item;
        }

        if (item) {
            const tagIds = item.tagIds || [];
            if (!tagIds.includes(tagId)) return;
            const tagIndex = tagIds.findIndex(el => el === tagId);
            if (tagIndex >= 0) tagIds.splice(tagIndex, 1);

            // update item
            //item.tagIds = tagIds;
            mutations.taskUpdate(state, {
                taskAuth,
                params: {
                    //name: item.name += "<p>#" + tag.name + "</p>",
                    tagIds,
                }
            });

            // update editor doc
            /*const provider = new HocuspocusProvider({
                url: Config.API_SERVER_WS_EDITOR_URI,
                name: taskAuth,
                onConnect() {
                    const doc = provider.document;
                    console.log("Provider connected: "+taskAuth, provider.document);

                    // TODO apply to live editor
                    //Y.applyUpdate(doc, diff2)
                },
            });*/

            // broadcast to subscribers of this
            payload.broadcasts = _broadcastsForTask(state, item);
            console.log("broadcast", payload.broadcasts);

            if (payload.initiatorAuth) {
                uiMutations.uiSetActorOfTask(state, {
                    userAuth: payload.userAuth,
                    subjectAuth: item.auth,
                    icon: "mdi-at",
                });
            }
        }
    },
    taskUpdateTags: (state, payload) => {
        console.log("MUTATION task/updateTags", payload);
        //if (!payload?.taskAuth) return;
        //if (!payload?.tagId) return;
        //if (!payload?.assigneeAuth&&!payload?.ownerAuth) return;

        /*const currentTagIds = item.tagIds || [];
        tagIds.forEach(tagId => {
            const tag = state.session.area.tags?.find(el => el.id === tagId);
            if (!tag) return;
            if (currentTagIds.includes(tagId)) return;
            currentTagIds.push(tagId);
        });

        // update item
        mutations.taskUpdate(state, {
            taskAuth,
            params: {
                //name: item.name += "<p>#" + tag.name + "</p>",
                tagIds: currentTagIds,
            }
        });*/
    },
    taskSearch: (state, payload) => {
        console.log("MUTATION task/search", payload);

        /*const item = payload?.contextAppend?.item;
        if (!item) return;

        // TODO should we use context/append instead of this?

        const area = state.session.area;
        let items = area.picks;

        // insert item to picks
        if (items?.findIndex(el => el.id === item.id) === -1) {
            items.push(item);
            //console.log("APPENDED", item, items);
        }*/
    },
    /*taskNote: (state, payload) => {
        console.log("MUTATION task/note", payload);
        if (!payload?.taskAuth) return;

        const taskAuth = payload.taskAuth;
        const isNote = !!payload.isOn;

        const area = state.session.area;
        const tasks = state.page.tasks.items;

        const updateTask = (task) => {
            task.isNote = isNote;
            task.prio = isNote ? PRIOS.NOTE : PRIOS.NONE;
        }

        // update item in a context
        let item;
        if (tasks?.length) {
            item = tasks.find(el => el.auth === taskAuth);
            if (item) updateTask(item);
        }
        if (state.page.task.item && state.page.task.item.auth === taskAuth) {
            updateTask(state.page.task.item);
            if (!item) item = state.page.task.item;
        }

        if (item) {
            if (payload.initiatorAuth) {
                uiMutations.uiSetActorOfTask(state, {
                    userAuth: payload.userAuth,
                    subjectAuth: item.auth,
                    icon: "mdi-at",
                });
            }
        }
    },*/

    /**
     * @deprecated
     * @param state
     * @param payload
     */
    /*taskGroup: (state, payload) => {
        console.log("MUTATION task/group", payload);
        if (!payload.taskAuth) return;

        const auth = payload.taskAuth;
        const items = state.page.tasks.items;
        //console.log("items", items);

        const newTopic = payload.topicAuth ? state.session.area.topics.find(el => el.auth === payload.topicAuth) : null;
        const newGroup = payload.groupAuth ? state.session.area.groups.find(el => el.auth === payload.groupAuth) : null;

        // 1. remove from current tasks
        let movedItem;
        if (items?.length) {
            const index = items.findIndex(el => el.auth === auth);
            if (index >= 0) {
                movedItem = items[index];

                /!*console.log("GROUP", state.page.context.group?.auth);
                console.log("TOPIC", state.page.context.topic?.auth);
                console.log("NEW TOPIC", newTopic?.auth);
                console.log("NEW GROUP", newGroup?.auth);*!/
                console.log("route", router.currentRoute.name);
                const currentRoute = router.currentRoute.name;

                if ((currentRoute === "my" || currentRoute === "my-task")
                    && state.page.context.assignee?.id === movedItem.userId) {
                    // this is a move from user's list to some group or topic
                    // change group/topic of the item - but not remove

                    if (newTopic) {
                        movedItem.topicId = newTopic?.id;
                        movedItem.groupId = state.session.area.groups.find(el => el.auth === newTopic.groupAuth)?.id;
                    } else if (newGroup) {
                        movedItem.topicId = null;
                        movedItem.groupId = newGroup.id;
                    }
                    console.log("Skipped removing as user's personal task");
                } else if ((currentRoute === "topic" || currentRoute === "topic-task")
                    && newTopic && newTopic.auth === state.page.context.topic?.auth) {
                    // should not move from topic to same topic
                    console.log("Skipped moving to same topic");
                } else if ((currentRoute === "group" || currentRoute === "group-task")
                    && (newTopic && newTopic.groupId === movedItem.groupId || newGroup?.id === movedItem.groupId)
                ) {
                    // this is a move to same group but another topic
                    // change topic of the item - but not remove
                    movedItem.topicId = newTopic?.id;
                    console.log("Topic updated in same group");
                } else {
                    console.log("REMOVING ITEM", movedItem);

                    const action = () => {
                        items.splice(index, 1);
                    };

                    if (payload.initiatorAuth) {
                        uiMutations.uiSetActorOfTask(state, {
                            userAuth: payload.userAuth,
                            subjectAuth: movedItem.auth,
                            icon: "mdi-close",
                        });
                        setTimeout(action, 600);
                    } else action();
                    //items.splice(index, 1);
                }
            }
        }
        if (movedItem) {
            const broadcasts = [];
            const group = state.session.area.groups.find(el => el.id === movedItem.groupId);
            if (group) {
                broadcasts.push({
                    entity: "group",
                    auth: group.auth,
                });
                const topic = movedItem.topicId ? state.session.area.topics.find(el => el.id === movedItem.topicId) : null;
                if (topic) broadcasts.push({
                    entity: "topic",
                    auth: topic.auth,
                });
            }
            if (newGroup) {
                broadcasts.push({
                    entity: "group",
                    auth: newGroup.auth,
                });
            }
            if (newTopic) broadcasts.push({
                entity: "topic",
                auth: newTopic.auth,
            });

            if (broadcasts.length) payload.broadcasts = broadcasts;
            console.log("broadcasts", broadcasts);

            uiMutations.uiSetActorOfGroup(state, {
                userAuth: payload.userAuth,
                subjectAuth: newTopic?.auth || newGroup?.auth,
                icon: "mdi-flare",
            });
        }

        // 2. append current tasks
        if (payload.newTask) {
            const group = state.page.context.group;
            const topic = state.page.context.topic;
            if ((payload.newTask.topicId && topic && payload.newTask.topicId === topic.id)
                || (payload.newTask.groupId && !topic && group && payload.newTask.groupId === group.id)) {
                //console.log("NEW TASK", payload.newTask);
                mutations.taskAppend(state, {
                    task: {
                        item: payload.newTask
                    }
                });

                uiMutations.uiSetActorOfTask(state, {
                    userAuth: payload.userAuth,
                    subjectAuth: payload.newTask.auth,
                    icon: "mdi-flare",
                });
            }
        }
    },*/
    /*taskMsgs: (state, payload) => {
        console.log("MUTATION task/msgs", payload);
        if (!payload?.msg?.item.todoId) return;

        // are we in needed task?
        const taskId = payload.msg.item.todoId;
        //console.log("taskId", taskId);
        //console.log("state.page.task.item.id", state.page.task.item?.id);
        if (state.page.task.item?.id === taskId) {
            state.page.comments.items = payload.comments.items;
        }
    },*/

    /**
     * This does not broadcast.
     * @param state
     * @param payload
     */
    taskSetUploads: (state, payload) => {
        console.log("MUTATION task/setUploads", payload);
        if (!payload) return;

        Vue.set(state.page.task, "uploads", payload);
    },
};

const _broadcastsForTask = (state, task) => {
    if (!task) return null;

    const broadcasts = [{
        entity: "task",
        auth: task.auth,
    }];
    //console.log("ITEM", item);
    const group = state.session.area.groups.find(el => el.id === task.groupId);
    if (group) {
        broadcasts.push({
            entity: "group",
            auth: group.auth,
        });

        let topic = task.topicId ? state.session.area.topics.find(el => el.id === task.topicId) : null
        if (topic) broadcasts.push({
            entity: "topic",
            auth: topic.auth,
        });
    }

    // broadcast to user
    if (task.userId) {
        const assignee = state.session.area.users.find(el => el.id === task.userId);
        if (assignee) {
            broadcasts.push({
                entity: "user",
                auth: assignee.id,
            });
        }
    }
    return broadcasts;
}

export default mutations;
